import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';

import { getFlows } from 'src/utils/flows.utils';

import { Flows } from 'Internals/flows';

import type { Flow } from '../types/flows.types';
import type { RootState } from 'Types/state.types';

export type FlowListActions =
  | 'initial'
  | 'loading-pipes'
  | 'pipes-loaded'
  | 'calculating-flows'
  | 'calculating-flows-finished-with-none-flows'
  | 'calculating-flows-finished-with-flows';

export const pipeStateLoaded = createSelector(
  (state: RootState) => Object.keys(state.pipes).length,
  (numOfPipes) => {
    // 1 because sometimes when in a pipe view, a single pipe gets loaded first
    // so the four maps here all have one element, but that's not
    // what we want to mean that it's finished loading
    // another place where changing the entire loading structure (maybe using react-query)
    // would avoid this issue
    return numOfPipes > 1;
  }
);

export const useFlows = () => {
  const [status, setStatus] = useState<FlowListActions>('initial');
  const [arePipeLoaded, setArePipeLoaded] = useState(false);
  const pipes = useSelector((state: RootState) => state.pipes);
  const pipeLoading = useSelector((state: RootState) => state.loadStatus.pipeLoading);

  const downstreams = useSelector((state: RootState) => state.downstreams);
  const lookups = useSelector((state: RootState) => state.lookups);
  const initialLoadFinished = useSelector(pipeStateLoaded);
  const [flows, setFlows] = useState<Flow[]>([]);

  useEffect(() => {
    if (!initialLoadFinished) {
      setStatus('loading-pipes');
      return;
    }

    setStatus('pipes-loaded');
    setStatus('calculating-flows');

    Flows.get(pipes, downstreams, lookups).then((resolvedFlows) => {
      const finalFlows = getFlows(resolvedFlows, pipes);
      setFlows(finalFlows);
      setArePipeLoaded(!isEmpty(pipes));
      setStatus(
        finalFlows.length === 0
          ? 'calculating-flows-finished-with-none-flows'
          : 'calculating-flows-finished-with-flows'
      );
    });
  }, [initialLoadFinished, pipes, downstreams, lookups]);

  return { status, flows, arePipeLoaded, pipeLoading };
};
